export const THEME_ATTRIBUTES = {
  mode: 'data-mode',
  brand: 'data-brand',
  density: 'data-density',
  style: 'data-style',
  a11y: 'data-a11y',
} as const;

export type ThemeAxis = keyof typeof THEME_ATTRIBUTES;

export const THEME_MODES = ['light', 'dark'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

// `system` is the ABSENCE of the attribute, not a value the CSS matches:
// data-mode="system" matches none of the three scopes and resolves to light.
export const THEME_MODE_PREFERENCES = ['system', ...THEME_MODES] as const;
export type ThemeModePreference = (typeof THEME_MODE_PREFERENCES)[number];

export const BRANDS = ['default'] as const;
export type Brand = (typeof BRANDS)[number];

export const DENSITIES = ['comfortable', 'compact', 'spacious'] as const;
export type Density = (typeof DENSITIES)[number];

// `auto` is the absence of the attribute, and is not the same as `comfortable`:
// :root:not([data-density]) is what gives touch devices the roomier scale.
export const DENSITY_PREFERENCES = ['auto', ...DENSITIES] as const;
export type DensityPreference = (typeof DENSITY_PREFERENCES)[number];

export const STYLE_PRESETS = ['default', 'swiss-clean'] as const;
export type StylePreset = (typeof STYLE_PRESETS)[number];

export const A11Y_FLAGS = ['high-contrast'] as const;
export type A11yFlag = (typeof A11Y_FLAGS)[number];

export interface ThemeSelection {
  mode: ThemeModePreference;
  brand: Brand;
  density: DensityPreference;
  style: StylePreset;
  a11y: readonly A11yFlag[];
}

export const THEME_DEFAULTS: ThemeSelection = {
  mode: 'system',
  brand: 'default',
  density: 'auto',
  style: 'default',
  a11y: [],
};

const oneOf =
  <T extends string>(allowed: readonly T[]) =>
  (value: unknown): value is T =>
    typeof value === 'string' && (allowed as readonly string[]).includes(value);

export const isThemeModePreference = oneOf(THEME_MODE_PREFERENCES);
export const isBrand = oneOf(BRANDS);
export const isDensityPreference = oneOf(DENSITY_PREFERENCES);
export const isStylePreset = oneOf(STYLE_PRESETS);
export const isA11yFlag = oneOf(A11Y_FLAGS);

export function parseThemeSelection(input: unknown): ThemeSelection {
  const source: Record<string, unknown> =
    typeof input === 'object' && input !== null ? (input as Record<string, unknown>) : {};

  const a11y = Array.isArray(source['a11y']) ? source['a11y'].filter(isA11yFlag) : [];

  return {
    mode: isThemeModePreference(source['mode']) ? source['mode'] : THEME_DEFAULTS.mode,
    brand: isBrand(source['brand']) ? source['brand'] : THEME_DEFAULTS.brand,
    density: isDensityPreference(source['density']) ? source['density'] : THEME_DEFAULTS.density,
    style: isStylePreset(source['style']) ? source['style'] : THEME_DEFAULTS.style,
    a11y,
  };
}

export function attributeValue(axis: ThemeAxis, selection: ThemeSelection): string | null {
  switch (axis) {
    case 'mode':
      return selection.mode === 'system' ? null : selection.mode;
    case 'density':
      return selection.density === 'auto' ? null : selection.density;
    case 'a11y':
      return selection.a11y.length > 0 ? selection.a11y.join(' ') : null;
    case 'brand':
      return selection.brand;
    default:
      return selection.style;
  }
}

export function applyTheme(root: Element, selection: ThemeSelection): void {
  for (const axis of Object.keys(THEME_ATTRIBUTES) as ThemeAxis[]) {
    const attribute = THEME_ATTRIBUTES[axis];
    const value = attributeValue(axis, selection);

    if (value === null) {
      root.removeAttribute(attribute);
    } else {
      root.setAttribute(attribute, value);
    }
  }
}
